/**
 * The Blueprint / Timeline / Uploaded Documents tabs (T5.1, T5.4, T7.24).
 *
 * The tabs sit in the app-wide tab bar, but their first label is the blueprint
 * term, and that is only known below BlueprintRoute's TermsProvider. So the bar
 * (in Chrome) offers an empty slot and the route portals the tabs into it. A
 * portal keeps React context, which is what carries the blueprint's own words
 * across; rendering the tabs in Chrome would show the system default word for a
 * client who has renamed it.
 *
 * Which tab is showing is `view` in the theme context, because the theme is per
 * tab (Blueprint dark, Timeline dark, Uploaded Documents light) and the two must
 * move together.
 *
 * The third tab's key is still `arch`. It is the stored theme key and the one
 * `ViewKey` in theme.tsx names, so renaming it would silently reset the theme
 * every device had chosen for that tab. Only the label changed (T7.24).
 */
import { createContext, useContext, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTerms } from './terms';
import { useTheme, type ViewKey } from './theme';

const SlotContext = createContext<{
  slot: HTMLElement | null;
  setSlot: (el: HTMLElement | null) => void;
}>({ slot: null, setSlot: () => {} });

export function ViewTabsSlotProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  return <SlotContext.Provider value={{ slot, setSlot }}>{children}</SlotContext.Provider>;
}

/** Rendered once by the tab bar. `display:flex` with the bar's own gap, so the tabs sit as they did. */
export function ViewTabsSlot() {
  const { setSlot } = useContext(SlotContext);
  return <span className="tabs-slot" role="tablist" ref={setSlot} />;
}

/**
 * All three tabs, always. Uploaded Documents replaced Architecture in T7.24 and
 * is available on every blueprint, so the `showArchitecture` flag that kept the
 * old tab absent for all but the seeded one (D-30) has nothing left to gate.
 */
export function ViewTabs() {
  const { slot } = useContext(SlotContext);
  const { view, setView } = useTheme();
  const terms = useTerms();
  if (!slot) return null;

  const tabs: { key: ViewKey; label: string }[] = [
    { key: 'fp', label: terms.noun('blueprint') },
    { key: 'tl', label: 'Timeline' },
    { key: 'arch', label: 'Uploaded Documents' },
  ];
  return createPortal(
    tabs.map((t) => (
      <button
        key={t.key}
        type="button"
        role="tab"
        aria-selected={view === t.key}
        className={view === t.key ? 'active' : undefined}
        onClick={() => setView(t.key)}
      >
        {t.label}
      </button>
    )),
    slot,
  );
}
